export type Market = 'cz' | 'sk' | 'international';

export type VatStatus = 'none' | 'valid' | 'invalid' | 'unavailable' | 'manual_payer' | 'manual_nonpayer' | 'informative';

export interface CustomerState {
    name: string;
    surname: string;
    email: string;
    phone: string | null;
    street: string;
    city: string;
    zip: string;
    company: string | null;
    ic: string | null;
    dic: string | null;
    hasCompanyData: boolean;
    isPartnerLocked: boolean;
    showContactEmail: boolean;
    contactEmail: string | null;
}

export interface DeliveryAddressOption {
    id: number;
    name: string;
    surname: string;
    company: string | null;
    street: string;
    city: string;
    zip: string;
    label: string;
}

export interface PickupPoint {
    type: 'branch' | 'ppl_evening' | 'cp_na_postu';
    fields: Partial<Record<'company' | 'street' | 'city' | 'zip', string>>;
    readonlyFields: string[];
}

export interface CheckoutState {
    basketReady: boolean;
    reason?: 'EMPTY_BASKET' | 'MISSING_COUNTRY' | 'MISSING_DELIVERY_PAYMENT';
    market: Market;
    countryId: number;
    countryName: string;
    customer: CustomerState | null;
    deliveryAddresses: DeliveryAddressOption[];
    pickupPoint: PickupPoint | null;
    vatState: { dic: string | null; status: VatStatus };
    adminAuth: boolean;
}

export interface BasketSummaryItem {
    kind: 'product' | 'gift' | 'delivery' | 'payment' | 'voucher' | 'giftChild';
    name: string;
    amount: number;
    amountUnit: string;
    free: boolean;
    /** ceny formátované server-side (stejný |currency filtr jako Latte) */
    priceWithTax: string | null;
    priceWithoutTax: string | null;
    imageUrl: string | null;
    productUrl: string | null;
    availability: string | null;
    outOfStock: boolean;
    pickupIcon: boolean;
}

export interface BasketSummary {
    items: BasketSummaryItem[];
    totalWithTax: string;
    totalWithoutTax: string;
    /** reverse-charge (skutečně bez DPH na objednávce) — SK/int ověřený plátce */
    reverseCharge: boolean;
    /** zvýraznit ceny bez DPH v souhrnu (reverse-charge NEBO plátce na CZ — tam jen zobrazení) */
    highlightWithoutTax: boolean;
    vatReason: string;
    loggedIn: boolean;
}

export interface VatValidateResponse {
    status: 'valid' | 'invalid' | 'unavailable' | 'not_applicable';
    normalizedDic: string;
    company: { ic?: string | null; companyName: string | null; street: string | null; city: string | null; zip: string | null } | null;
    reverseCharge: boolean;
    countryMismatch: boolean;
    message: string | null;
    basketSummary: BasketSummary;
}

export interface CompanyLookupResponse {
    found: boolean;
    source: string;
    company: { ic?: string; dic?: string; companyName?: string; street?: string; city?: string; zip?: string } | null;
    addressVariants: {
        registry: Partial<Record<'street' | 'city' | 'zip', string>>;
        vies: Partial<Record<'street' | 'city' | 'zip', string>>;
    } | null;
    groupVatWarning: boolean;
}

export interface OrderSuccessResponse {
    orderId: number;
    orderNum: string | null;
    redirectUrl: string;
}

export interface CheckoutFormValues {
    person: {
        name: string;
        surname: string;
        email: string;
        phone: string;
        wantContactEmail: boolean;
        contactEmail: string;
    };
    billing: { street: string; city: string; zip: string };
    delivery_open: boolean;
    delivery: { company: string; name: string; surname: string; street: string; city: string; zip: string };
    company_open: boolean;
    company: { name: string; ic: string; dic: string; referee: string };
    note: string;
    agreements: {
        vop: boolean;
        createAccount: boolean;
        registration: boolean;
        newsletter: boolean;
        heureka: boolean;
    };
    testing: boolean;
}

export interface SmartformConfig {
    scriptUrl: string;
    clientId: string;
    country: 'CZ' | 'SK';
}

export interface CheckoutProps {
    apiBaseUrl: string;
    /** URL gettext .mo katalogu pro JS translator (NULL = katalog neexistuje) */
    translationsUrl: string | null;
    /** Smartform našeptávač adres — jen CZ/SK trhy, NULL = vypnuto */
    smartform: SmartformConfig | null;
    initialState: CheckoutState;
    links: { back: string; terms: string; privacy: string };
}

/** Klientská kontrola e-mailu — server je autorita (Validators::isEmail). */
export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Pevná telefonní předvolba lokálních trhů — staré weby bola-cz/bola-sk ji měly u telefonu natvrdo. */
export const PHONE_PREFIXES: Partial<Record<Market, string>> = { cz: '+420', sk: '+421' };

/** Odstraní předvolbu trhu z uloženého čísla pro zobrazení v národním tvaru. */
export function stripPhonePrefix(market: Market, phone: string): string {
    const prefix = PHONE_PREFIXES[market];
    if (!prefix) {
        return phone;
    }
    if (phone.startsWith(prefix)) {
        return phone.slice(prefix.length);
    }
    const zeroPrefix = '00' + prefix.slice(1);
    return phone.startsWith(zeroPrefix) ? phone.slice(zeroPrefix.length) : phone;
}

/** IČ bez mezer a předpony státu — zákazníci vkládají „CZ12345678" nebo „123 45 678". */
export function normalizeIc(value: string): string {
    return value.replace(/\s+/g, '').replace(/^(CZ|SK)/i, '');
}

export const IC_PATTERN = /^[0-9]{8}$/;

/** Složí číslo s předvolbou trhu; číslo už v mezinárodním tvaru nechává být. */
export function joinPhonePrefix(market: Market, phone: string): string {
    const prefix = PHONE_PREFIXES[market];
    const trimmed = phone.trim();
    if (!prefix || trimmed === '' || trimmed.startsWith('+') || trimmed.startsWith('00')) {
        return trimmed;
    }
    return prefix + trimmed.replace(/^0/, '');
}
